import React, { useState, useEffect, useContext } from "react";
import { Selection, Label } from "@fluentui/react";
import { ISLEntryGridItem } from "../../schemas/SLEntryGridSchemas";
import { css } from "@emotion/css";
import SLEntriesGridCommands from "./SLEntriesGridCommands";
import { ExportToExcel } from "../../services/ExportToExcel";
import { attachmentUtil } from "../../services/AttachmentUtil";
import {
  postAttachment,
  patchAttachments,
  patchBatchAttachments,
  IPostAttachmentResponse
} from "./SLEntriesGridRequests";

import LoadingSpinner from "../../baseComponents/LoadingSpinner";
import { INotificationMessagesRef } from "../NotificationMessages";
import { ISLBatchPopupOverlay } from "../SLBatchPopup/SLBatchPopup";
import { ISLActionsManager } from "../SLActionsManager/SLActionsManager";
import { ActionTypes } from "../SLActionsManager/SLActionsManagerTypes";
import SLEntriesDetails, { IProcessingStatus, ISLEntriesDetailsProps } from "../SLEntriesDetails";
import {
  selectionToEntryIds,
  selectionToEntry,
  GridCommandToggle,
  validateEntriesForAction,
  processingStatusToListOfListOfEntries
} from "./utilities";
import { UserContext } from "../../contexts/UserContext";
import { SubledgerContext } from "../../contexts/SubledgerContext";

export const SLEntriesGridHeaders = [
  "Name / Document#",
  "Company Code",
  "Description",
  "Status",
  "Posted By",
  "Poster(s)",
  "Reviewed By",
  "Fiscal Year/Period",
  "Comments",
  "Modified Date",
  "Format",
  "Attachments",
  "Processing Status"
];

export enum GridLoadingStatus {
  idle = "idle",
  pending = "pending",
  resolved = "resolved",
  rejected = "rejected"
}

export interface ISLEntriesGridProps {
  items: ISLEntryGridItem[];
  loading: GridLoadingStatus;
  batchPopup: ISLBatchPopupOverlay["ref"] | null;
  actionsManager: ISLActionsManager["ref"] | null;
  notificationsFn: INotificationMessagesRef["setMessage"];
  onActionCallback: (name?: ActionTypes) => any;
  routeState: ISLEntriesDetailsProps["routeState"];
  postDisabled: GridCommandToggle;
  addPostersDisabled: GridCommandToggle;
  exportEntriesDisabled: GridCommandToggle;
  sendToPosterDisabled: GridCommandToggle;
  approveEntriesDisabled: GridCommandToggle;
  needsClarificationDisabled: GridCommandToggle;
  recallEntriesDisabled: GridCommandToggle;
  deleteDraftsDisabled: GridCommandToggle;
  addAttachmentDisabled: GridCommandToggle;
  deleteAttachmentsDisabled: GridCommandToggle;
  useWeightageCalculator: boolean;
}

interface IFileSanitization {
  files: File[];
  errors: string[];
}

function postResultToPatchAttachment(data: IPostAttachmentResponse) {
  return [
    {
      blobName: data.blobName,
      fileName: data.inputFileName,
      url: data.fileStorageUri,
      fileSize: data.fileSize,
      uploadedOn: data.uploadedOn
    }
  ];
}

async function attachFilesAndAddToSlEntries(
  selection: Selection,
  sanitizedFilesAndErrors: IFileSanitization,
  token: () => Promise<any>
) {
  const success = [] as string[];
  const errors = [] as string[];
  const rowSelectedEntries = selectionToEntry(selection);
  const batchEntriesId = [] as number[];
  const batchEntriesRefguids = [] as string[];
  const slEntriesId = [] as number[];
  for (const entry of rowSelectedEntries) {
    if (
      entry.batchRefguid !== null &&
      entry.batchRefguid !== undefined &&
      entry.batchRefguid !== "" &&
      entry.format === "Batch" &&
      (entry.statusId === 1 || entry.statusId === 3 || entry.statusId === 5 || entry.statusId === 6)
    ) {
      batchEntriesId.push(entry.slEntryId);
      batchEntriesRefguids.push(entry.batchRefguid);
    } else {
      slEntriesId.push(entry.slEntryId);
    }
  }
  const rowsSelectedIds = selectionToEntryIds(selection);
  if (slEntriesId.length === 0 && batchEntriesId.length === 0) {
    return {
      success,
      errors: ["Please select rows from the grid."]
    };
  }
  for (const file of sanitizedFilesAndErrors.files) {
    try {
      const postResult = await postAttachment(file, rowsSelectedIds, token);
      if (slEntriesId.length > 0) await patchAttachments(postResultToPatchAttachment(postResult), slEntriesId, token);
      if (batchEntriesId.length > 0)
        await patchBatchAttachments(postResultToPatchAttachment(postResult), batchEntriesRefguids, token);
      const text = rowsSelectedIds.length === 1 ? "Entry" : "Entries";
      success.push(`Uploaded file ${file.name} for ${rowsSelectedIds.length} ${text}.`);
    } catch (_) {
      errors.push(`Could not upload ${file.name}.`);
    }
  }
  return {
    success,
    errors
  };
}

const initialHiddenState = {
  selection: new Selection()
};

const noEntriesStyle = css`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-content: center;
  align-items: center;
`;

const SLEntriesGrid = (props: ISLEntriesGridProps): JSX.Element | null => {
  const { accessToken } = useContext(UserContext);
  const { actionLinks } = useContext(SubledgerContext);
  const [state, setState] = useState(initialHiddenState);
  const [gridLoadingStatus, setGridLoadingStatus] = useState(GridLoadingStatus.pending);
  const [processingStatus, setProcessingStatus] = useState<IProcessingStatus["statuses"]>({});

  useEffect(() => {
    setState((previousState) => {
      return { ...previousState, selection: new Selection() };
    });
  }, [props.items]);

  useEffect(() => {
    setState((p) => {
      return {
        ...p,
        selection: new Selection()
      };
    });
  }, [props.items]);

  useEffect(() => {
    return () => {
      setState((p) => {
        return {
          ...p,
          selection: new Selection()
        };
      });
    };
  }, []);

  useEffect(() => {
    setGridLoadingStatus(props.loading);
  }, [props.loading]);

  const proceedWithAction = (
    name: ActionTypes,
    entries: ISLEntryGridItem[],
    actionsManager: ISLActionsManager["ref"]
  ) => {
    actionsManager.open(name, entries);
    const onActionFinished = actionsManager.getOnSubmitAsyncSubject();
    onActionFinished.subscribe((result) => {
      if (result !== null) {
        if (Object.keys(result.processingStatus).length !== 0 && "statuses" in result.processingStatus) {
          setProcessingStatus((result.processingStatus as IProcessingStatus).statuses);
        } else {
          setProcessingStatus({});
        }
        props.notificationsFn(result.type, result.messages);
        props.onActionCallback(name);
      }
    });
  };

  const actionsTrigger = (name: ActionTypes) => () => {
    if (state.selection.getSelectedCount() === 0) {
      props.notificationsFn("info", ["Please select an Entry."]);
    } else {
      if (props.actionsManager) {
        const entries = state.selection.getSelection() as ISLEntryGridItem[];
        if (props.useWeightageCalculator) {
          const statuses = validateEntriesForAction(actionLinks.weightages, name, entries);
          if (statuses === null) {
            proceedWithAction(name, entries, props.actionsManager);
          } else {
            setProcessingStatus(statuses);
            const invalidEntries = processingStatusToListOfListOfEntries(statuses);
            props.notificationsFn(
              "errors",
              [
                `Action "${name}" is not valid for some Selected Entries. Please remove the following entries from your selection.`
              ].concat(...invalidEntries)
            );
          }
        } else {
          proceedWithAction(name, entries, props.actionsManager);
        }
      }
    }
  };

  const onFileSelected = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const filesToAttach: File[] = Array.from(ev.target.files || []);
    const fileExtensions = ev.target.accept;
    const sanitizedFilesAndErrors: IFileSanitization = filesToAttach.reduce(
      (ctr, file) => {
        const sanitizedFilename = attachmentUtil.isValidFileExtension(file.name, fileExtensions);
        if (!sanitizedFilename) {
          ctr.errors.push(`Invalid file extension for ${file.name}.`);
        } else if (file.size === 0) {
          ctr.errors.push(`Empty (0 KB) file ${file.name} cannot be attached.`);
        } else {
          ctr.files.push(file);
        }
        return ctr;
      },
      { files: [], errors: [] } as IFileSanitization
    );
    const results = await attachFilesAndAddToSlEntries(state.selection, sanitizedFilesAndErrors, accessToken);
    if (results.success.length > 0) {
      props.onActionCallback();
    }
    sanitizedFilesAndErrors.errors = [...sanitizedFilesAndErrors.errors, ...results.errors];
    props.notificationsFn("errors", sanitizedFilesAndErrors.errors);
    props.notificationsFn("success", results.success, { preventReset: true });
  };

  const onExportExcel = () => {
    const itemsSelected = state.selection.getSelection() as ISLEntryGridItem[];
    const itemsToExport = itemsSelected.length === 0 ? props.items : itemsSelected;
    ExportToExcel(SLEntriesGridHeaders, itemsToExport, processingStatus);
  };

  const addAttachmentActionIsValidFunction = () => {
    const entries = state.selection.getSelection() as ISLEntryGridItem[];
    const statuses = validateEntriesForAction(actionLinks.weightages, "AddAttachments", entries);
    if (statuses === null) {
      setProcessingStatus({});
      return true;
    } else {
      setProcessingStatus(statuses);
      const invalidEntries = processingStatusToListOfListOfEntries(statuses);
      props.notificationsFn(
        "errors",
        [
          `Action "AddAttachments" is not valid for some Selected Entries. Please remove the following entries from your selection.`
        ].concat(invalidEntries)
      );
      return false;
    }
  };

  if (gridLoadingStatus === GridLoadingStatus.idle) {
    return null;
  } else if (gridLoadingStatus === GridLoadingStatus.pending) {
    return <LoadingSpinner label="Loading Grid" />;
  }

  return (
    <React.Fragment>
      <SLEntriesGridCommands
        hide={false}
        post={actionsTrigger("Post")}
        addPosters={actionsTrigger("AddPosters")}
        exportEntries={onExportExcel}
        sendToPoster={actionsTrigger("SendToPoster")}
        approveEntries={actionsTrigger("ApproveEntries")}
        needsClarification={actionsTrigger("NeedClarification")}
        recallEntries={actionsTrigger("RecallEntries")}
        deleteDrafts={actionsTrigger("Delete")}
        deleteAttachments={actionsTrigger("DeleteAttachments")}
        addAttachmentActionIsValidFunction={addAttachmentActionIsValidFunction}
        onFilesSelected={onFileSelected}
        postDisabled={props.postDisabled}
        addPostersDisabled={props.addPostersDisabled}
        exportEntriesDisabled={props.exportEntriesDisabled}
        sendToPosterDisabled={props.sendToPosterDisabled}
        approveEntriesDisabled={props.approveEntriesDisabled}
        needsClarificationDisabled={props.needsClarificationDisabled}
        recallEntriesDisabled={props.recallEntriesDisabled}
        deleteDraftsDisabled={props.deleteDraftsDisabled}
        addAttachmentDisabled={props.addAttachmentDisabled}
        deleteAttachmentsDisabled={props.deleteAttachmentsDisabled}
      />
      {props.items.length > 0 ? (
        <SLEntriesDetails
          routeState={props.routeState}
          items={props.items}
          selection={state.selection}
          processingStatus={processingStatus}
          batchPopup={props.batchPopup}
        />
      ) : (
        <Label className={noEntriesStyle}>No Subledger entries to display.</Label>
      )}
    </React.Fragment>
  );
};

SLEntriesGrid.displayName = "SLEntriesGrid";

export default SLEntriesGrid;
